import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { toastr } from 'react-redux-toastr';
import styled from 'styled-components';
import qs from 'qs';
import PropTypes from 'prop-types';
import { has, get, isEmpty, map, getOr, keys } from 'lodash/fp';
import {
  Button,
  ButtonToolbar,
  ControlLabel,
  Form,
  FormControl,
  HelpBlock,
  Icon
} from 'rsuite';
import {
  getIsAgeBreakdown,
  getMainSettingsData
} from '../../../redux/Settings/selectors/main';
import {
  getIsSaving,
  getIsSaved,
  getIsUploadingFile
} from '../../../redux/Guest/selectors/components/guest-form';
import { createGuest, updateGuest } from '../../../redux/Guest/sagas/routines';
import { CompressFormGroup } from '../../widgets/FormElements';
import { fetchSettings } from '../../../redux/Settings/sagas/main/routines';
import fieldPropsByType from '../../../helpers/fieldPropsByType';
import { parseInt } from '../../../helpers/utils';
import { ColumnSettingsType, GuestType } from '../../../interfaces/guest';
import {
  DefaultValues,
  createModelSchema,
  toFieldOptions,
  dataToFormValue,
  formValueToBody,
  showFieldsPerSettings,
  requiredFieldsPerSettings,
  composeAgeDefaultValues
} from './settings';
import FormErrorMessageList from '../FormErrorMessageList';
import { resetGuestForm } from '../../../redux/Guest/actions/components/guest-form';

const StyledControlLabel = styled(ControlLabel)`
  white-space: normal;
  width: 220px !important;
  font-size: 12px !important;
`;

const AgeGroupHelpBlock = styled(HelpBlock)`
  margin-left: 80px;
  margin-bottom: 4px;
  > span {
    margin-left: 22px;
  }
`;

const GuestForm = ({ data, section, formFields, onSaved, onCancel }) => {
  const dispatch = useDispatch();
  const formRef = useRef();
  const { search: queryString } = useLocation();
  const { redirectPath = '' } = useMemo(
    () => qs.parse(queryString, { ignoreQueryPrefix: true }),
    [queryString]
  );

  const [formValue, setFormValue] = useState(DefaultValues);
  const [formError, setFormError] = useState({});
  const isSaving = useSelector(getIsSaving);
  const isSaved = useSelector(getIsSaved);
  const isUploadingFile = useSelector(getIsUploadingFile);
  const settings = useSelector(getMainSettingsData);
  const isAgeBreakdown = useSelector(getIsAgeBreakdown);
  const isHomeless = parseInt(get('homeless', formValue)) === 1;
  const isContactForHelp = parseInt(get('contactForHelp', formValue)) === 2;
  const showFields = showFieldsPerSettings(settings, isHomeless);
  const requiredFields = requiredFieldsPerSettings(
    settings,
    keys(formFields),
    isContactForHelp
  );

  const handleFormChange = value => {
    setFormValue(value);
  };

  const handleFieldChange = field => value => {
    setFormValue({ ...formValue, [field]: value });
    if (value && has(field, formError)) {
      setTimeout(() => {
        formRef.current.check();
      }, 50);
    }
  };

  const handleReset = () => {
    setFormError({});
    setFormValue(dataToFormValue(data, formFields));
    setTimeout(() => {
      formRef.current.check();
    }, 50);
  };

  const handleSubmit = () => {
    if (formRef.current.check()) {
      const { id, ...rawFormValue } = formValue;
      const body = formValueToBody(rawFormValue, formFields);
      if (id) dispatch(updateGuest({ id, body, section }));
      else dispatch(createGuest({ body, redirectPath }));
    } else {
      toastr.error('Oops!', 'Unable to save. Please check form error.');
    }
  };

  const handleFormCheck = errors => {
    setFormError(errors);
  };

  const formModel = useMemo(
    () =>
      createModelSchema({
        formFields,
        requiredFields,
        isHomeless,
        isAgeBreakdown
      }),
    [formFields, requiredFields, isHomeless, isAgeBreakdown]
  );

  useEffect(
    () => {
      if (isEmpty(settings)) {
        dispatch(fetchSettings());
      } else {
        const agesDefaults = composeAgeDefaultValues(settings.ageGroupCount);
        setFormValue({ ...formValue, ...agesDefaults });
      }
    },
    [settings]
  );

  useEffect(
    () => {
      if (!isEmpty(data)) setFormValue(dataToFormValue(data, formFields));
    },
    [data, formFields]
  );

  useEffect(
    () => {
      if (isSaved) onSaved();
      return () => {
        dispatch(resetGuestForm());
      };
    },
    [isSaved]
  );

  return (
    <Form
      ref={formRef}
      layout="horizontal"
      model={formModel}
      formValue={formValue}
      onCheck={handleFormCheck}
      onChange={handleFormChange}
      style={{ overflow: 'hidden' }}
    >
      {!isEmpty(formError) && (
        <FormErrorMessageList
          formError={formError}
          fieldSettings={formFields}
        />
      )}
      <ButtonToolbar style={{ textAlign: 'right' }}>
        <Button onClick={onCancel} disabled={isSaving}>
          <Icon icon="close-circle" /> Cancel
        </Button>
        {formValue.id && (
          <Button onClick={handleReset} disabled={isSaving}>
            <Icon icon="refresh2" /> Reset
          </Button>
        )}
        <Button appearance="primary" onClick={handleSubmit} disabled={isSaving}>
          <Icon icon="save" /> {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </ButtonToolbar>
      {map(
        ({
          name,
          label,
          fieldType,
          isRequired,
          readOnly = false,
          disabled = false,
          options = []
        }) => {
          const showField = getOr(true, name, showFields);
          if (!showField || readOnly || !label) return '';

          const requiredField = getOr(isRequired, name, requiredFields);
          const hasError = has(name, formError);
          const fieldProps = getOr(() => ({}), fieldType, fieldPropsByType)({
            options: toFieldOptions(options),
            onChange: handleFieldChange(name)
          });

          return (
            <CompressFormGroup
              key={`field-${name}`}
              className={hasError && 'error'}
            >
              {name === 'age1' && (
                <AgeGroupHelpBlock>
                  Household Age Group:
                  <span># of Individual</span>
                </AgeGroupHelpBlock>
              )}
              <StyledControlLabel>
                {label}{' '}
                {requiredField && <span style={{ color: 'red' }}>*</span>}
              </StyledControlLabel>
              <FormControl
                {...fieldProps}
                name={name}
                disabled={disabled}
                readOnly={disabled}
                errorPlacement="rightStart"
              />
            </CompressFormGroup>
          );
        },
        formFields
      )}
      <ButtonToolbar style={{ textAlign: 'right' }}>
        <Button onClick={onCancel} disabled={isSaving || isUploadingFile}>
          <Icon icon="close-circle" /> Cancel
        </Button>
        {formValue.id && (
          <Button onClick={handleReset} disabled={isSaving || isUploadingFile}>
            <Icon icon="refresh2" /> Reset
          </Button>
        )}
        <Button
          appearance="primary"
          onClick={handleSubmit}
          disabled={isSaving || isUploadingFile}
        >
          <Icon icon="save" /> {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </ButtonToolbar>
    </Form>
  );
};

GuestForm.propTypes = {
  formFields: PropTypes.objectOf(ColumnSettingsType).isRequired,
  data: GuestType,
  section: PropTypes.oneOf(['primary', 'household']),
  onSaved: PropTypes.func,
  onCancel: PropTypes.func
};

GuestForm.defaultProps = {
  data: {},
  section: null,
  onSaved: () => {},
  onCancel: () => {}
};

export default GuestForm;
